/**
 * SubjectCapabilityProjection client (WS4 cutover).
 *
 * Replaces the per-plugin discovery probe storm (one PDP `experience.plugin`
 * evaluation per enabled plugin) with ONE capability-document fetch. The SPA
 * posts the manifest of protected UI resources once; the BFF forwards it to the
 * PDP-owned projection endpoint, which returns the version-stamped, cached
 * document of visible plugins/routes/widgets. PDP coalesces and caches by
 * version tuple across pods, so repeat/cold loads do not re-evaluate 82 probes.
 *
 * Security boundary: this governs VISIBILITY only. Every real action still hits
 * the PDP at action time. Failure => fail closed (empty visibility / safe shell).
 *
 * De-dupe: a single in-flight promise is shared across all callers (mega menu,
 * header widgets, routes) and the result is cached per subject for the session,
 * so LayoutShell/Home/header mounting in the same tick issue ONE request.
 */
import { getAuthzenSubject } from './subject';
import { fetchSessionOnce } from '../session/SessionSingleFlight';
import type { PluginManifest } from '../../plugins/types';

export interface CapabilityProjection {
	subject: string;
	app: string;
	visible_plugins: string[];
	visible_routes: string[];
	visible_widgets: string[];
	capability_tags: string[];
	denied_summary: string[];
	degraded: boolean;
	policy_version?: string;
	membership_version?: string;
	manifest_version?: string;
	config_version?: string;
	subject_claims_version?: string;
	generated_at?: string;
	expires_at?: string;
	etag?: string;
}

interface UIResourcePayload {
	plugin_id: string;
	resource_type: string;
	resource_id: string;
	action: string;
	pdp_application: string;
	tier: number;
	kind: string;
	/** Emitted into `capability_tags` by the projection when the subject is allowed. */
	capability_tag?: string;
}

const APP_ID = 'experience';

// Domains whose KPIs are gated by a `<domain>.metrics.view` capability tag (see
// `BFF/config/monitoring_snapshot.yaml`). The Home "Your stats" strip and the
// KPI gallery are protected controls: they render only when the subject holds at
// least one of these tags.
const METRICS_CAPABILITY_DOMAINS = ['platform', 'pdp', 'idp', 'iga', 'mcp', 'crud'] as const;

/**
 * Capability resources that make the metrics-view tags a governed control.
 *
 * Each maps to the operations-center metrics boundary gate
 * (`op:operations-center:metrics.view` — resource `operations:metrics`, action
 * `view`), which app owners already grant to app-roles via the operations-center
 * easy-lane. The projection probes each and, on allow, emits its `capability_tag`
 * into `capability_tags`. One resource per domain so a single `metrics.view`
 * grant lights up all `<domain>.metrics.view` tags the KPI catalog filters on.
 * `tier: 3` (action / capability-tag only) + `kind: 'widget'` keeps them out of
 * the plugin/route visibility lists — they contribute a tag, nothing else.
 */
export function metricsCapabilityResources(): UIResourcePayload[] {
	return METRICS_CAPABILITY_DOMAINS.map((domain) => ({
		plugin_id: 'operations-center',
		resource_type: 'operations:metrics',
		resource_id: 'operations:metrics',
		action: 'view',
		pdp_application: 'operations-center',
		tier: 3,
		kind: 'widget',
		capability_tag: `${domain}.metrics.view`,
	}));
}
// Session-scoped reuse: the projection is version-stamped server-side, so a
// modest client TTL is safe and only bounds how quickly UI visibility reacts to
// a revocation (action enforcement is always live).
const PROJECTION_TTL_MS = 60_000;

type CachedProjection = { ts: number; projection: CapabilityProjection };
const cacheByAuthContext = new Map<string, CachedProjection>();
const inFlightByAuthContext = new Map<string, Promise<CapabilityProjection>>();

function isTestMode(): boolean {
	return (import.meta as any).env?.MODE === 'test';
}

function getUserArn(session: any): string | null {
	const direct =
		session?.user_arn ??
		session?.user?.user_arn ??
		session?.profile?.user_arn ??
		session?.['https://schemas.empowernow.dev/identity/account_arn'] ??
		session?.user?.['https://schemas.empowernow.dev/identity/account_arn'] ??
		null;
	if (typeof direct === 'string' && direct.trim().length > 0) return direct.trim();
	return null;
}

function getCsrfToken(): string {
	return document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1] || '';
}

export function buildResources(plugins: PluginManifest[]): UIResourcePayload[] {
	const pluginResources = plugins.map((plugin) => {
		const pluginResource = plugin.authorization?.resources?.find(
			(r) => r.type === 'experience.plugin',
		);
		const resourceId = pluginResource?.id || `plugin:${plugin.id}`;
		const pdpApp = plugin.authorization?.pdp_application || APP_ID;
		return {
			plugin_id: plugin.id,
			resource_type: 'experience.plugin',
			resource_id: resourceId,
			action: 'access',
			pdp_application: pdpApp,
			// Protected feature boundary (Tier 2): projected via a PDP decision.
			tier: 2,
			kind: 'plugin',
		};
	});
	// Metrics-view capability tags are a shell-owned protected control, not tied
	// to a single plugin's visibility — append them to every projection request.
	return [...pluginResources, ...metricsCapabilityResources()];
}

function emptyProjection(subject: string, degraded: boolean): CapabilityProjection {
	return {
		subject,
		app: APP_ID,
		visible_plugins: [],
		visible_routes: [],
		visible_widgets: [],
		capability_tags: [],
		denied_summary: [],
		degraded,
	};
}

async function requestProjection(
	subject: string,
	plugins: PluginManifest[],
): Promise<CapabilityProjection> {
	const base = (import.meta as any).env?.VITE_BFF_BASE_URL || '';
	const csrf = getCsrfToken();
	const body = {
		subject,
		app: APP_ID,
		resources: buildResources(plugins),
	};
	// Use the `/api/` prefix: the SPA edge (nginx) only proxies `/api/`, `/auth/`,
	// and `/access/` to the BFF. `/api/me/capabilities` is the BFF SDK alias for
	// the PDP capability projection; a bare `/me/...` would hit the SPA static
	// handler and 405.
	const res = await fetch(`${base}/api/me/capabilities`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'X-CSRF-Token': csrf,
			'X-CSRFToken': csrf,
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		// Fail closed: a failed projection yields no protected visibility.
		console.warn(`[capabilities] projection fetch failed: ${res.status}`);
		return emptyProjection(subject, true);
	}
	const data = await res.json();
	return {
		subject: data.subject ?? subject,
		app: data.app ?? APP_ID,
		visible_plugins: data.visible_plugins ?? [],
		visible_routes: data.visible_routes ?? [],
		visible_widgets: data.visible_widgets ?? [],
		capability_tags: data.capability_tags ?? [],
		denied_summary: data.denied_summary ?? [],
		degraded: data.degraded ?? false,
		policy_version: data.policy_version,
		membership_version: data.membership_version,
		manifest_version: data.manifest_version,
		config_version: data.config_version,
		subject_claims_version: data.subject_claims_version,
		generated_at: data.generated_at,
		expires_at: data.expires_at,
		etag: res.headers.get('ETag') || undefined,
	};
}

/**
 * Fetch the capability projection for the current subject (globally de-duped).
 *
 * @param plugins Enabled plugin manifests to project visibility over.
 */
export async function fetchCapabilityProjection(
	plugins: PluginManifest[],
): Promise<CapabilityProjection> {
	const session = await fetchSessionOnce();
	if (!(session as any)?.authenticated) return emptyProjection('', false);
	const userArn = getUserArn(session);
	if (!userArn) return emptyProjection('', false);

	// Never project over an empty resource set. An empty request is meaningless
	// (it projects visibility over nothing) and, against older servers, could be
	// cached as an empty result. Callers that race ahead of plugin-manifest load
	// pass [] here; return an uncached empty projection instead of round-tripping.
	if (!plugins || plugins.length === 0) return emptyProjection('', false);

	const subject = await getAuthzenSubject();
	const subjectId =
		typeof subject === 'string' ? subject : (subject as any)?.id || userArn;
	const key = `user_arn:${userArn}`;
	const now = Date.now();

	if (!isTestMode()) {
		const cached = cacheByAuthContext.get(key);
		if (cached && now - cached.ts < PROJECTION_TTL_MS) return cached.projection;
		// Register the in-flight promise BEFORE awaiting so concurrent callers in
		// the same tick share one request (fixes the discovery TOCTOU).
		const inFlight = inFlightByAuthContext.get(key);
		if (inFlight) return inFlight;
	}

	const load = requestProjection(subjectId, plugins);
	if (!isTestMode()) inFlightByAuthContext.set(key, load);
	try {
		const projection = await load;
		if (!isTestMode()) cacheByAuthContext.set(key, { ts: Date.now(), projection });
		return projection;
	} finally {
		if (!isTestMode()) inFlightByAuthContext.delete(key);
	}
}

/** Invalidate the cached projection (call on auth/grant change / logout). */
export function invalidateCapabilityProjection(): void {
	cacheByAuthContext.clear();
	inFlightByAuthContext.clear();
}
